import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import { Support as SupportIcon } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { logger } from '@/utils/logger';

const defaultConfig = {
  contact: {
    email: 'suporte@vover.com.br',
    whatsapp: '(11) 99999-9999',
    hours: 'Segunda a Sexta, 9h às 18h'
  },
  messages: {
    developmentFeatureTitle: 'Funcionalidade em Desenvolvimento',
    developmentFeatureDescription:
      'A geração automática de boletos e PIX está em desenvolvimento e será disponibilizada em breve.',
    contactInstruction: 'Para ativar sua assinatura, entre em contato com nosso suporte:',
    assuranceMessage:
      'Nossa equipe irá ajudá-lo a processar o pagamento e ativar sua assinatura imediatamente.'
  }
};

export const SupportConfigPage: React.FC = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    setRefreshCallback(loadConfig);
    return () => setRefreshCallback(null);
  }, [setRefreshCallback]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSupportConfig();
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setConfig({
          contact: {
            email: data.contact?.email ?? defaultConfig.contact.email,
            whatsapp: data.contact?.whatsapp ?? defaultConfig.contact.whatsapp,
            hours: data.contact?.hours ?? defaultConfig.contact.hours
          },
          messages: {
            developmentFeatureTitle:
              data.messages?.developmentFeatureTitle ??
              defaultConfig.messages.developmentFeatureTitle,
            developmentFeatureDescription:
              data.messages?.developmentFeatureDescription ??
              defaultConfig.messages.developmentFeatureDescription,
            contactInstruction:
              data.messages?.contactInstruction ?? defaultConfig.messages.contactInstruction,
            assuranceMessage:
              data.messages?.assuranceMessage ?? defaultConfig.messages.assuranceMessage
          }
        });
      } else {
        setConfig({ ...defaultConfig });
      }
    } catch (error) {
      logger.error('Error loading support config:', error);
      setConfig({ ...defaultConfig });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const response = await apiService.updateSupportConfig(config);
      if (response.data.success) {
        showSuccess('Configuração salva com sucesso!');
        setConfig(response.data.data || config);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Alert severity="warning">Não foi possível carregar a configuração. Tente novamente.</Alert>
    );
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportIcon color="primary" />
              <Typography variant="h4">Configuração de Suporte</Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Informações exibidas ao usuário em fluxos que exigem atendimento humano, bloqueio por
              cobrança ou funcionalidades em implantação.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" label="Contato ao cliente" />
              <Chip size="small" variant="outlined" label="Mensagens do modal" />
              <Chip size="small" variant="outlined" label="Escopo global" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Contato do Suporte
        </Typography>
        <TextField
          fullWidth
          label="E-mail"
          type="email"
          value={config.contact?.email || ''}
          onChange={e =>
            setConfig({
              ...config,
              contact: { ...config.contact, email: e.target.value }
            })
          }
          margin="normal"
          helperText="Ex: suporte@vover.com.br"
        />
        <TextField
          fullWidth
          label="WhatsApp"
          value={config.contact?.whatsapp || ''}
          onChange={e =>
            setConfig({
              ...config,
              contact: { ...config.contact, whatsapp: e.target.value }
            })
          }
          margin="normal"
          helperText="Ex: (11) 99999-9999"
        />
        <TextField
          fullWidth
          label="Horário de atendimento"
          value={config.contact?.hours || ''}
          onChange={e =>
            setConfig({
              ...config,
              contact: { ...config.contact, hours: e.target.value }
            })
          }
          margin="normal"
          helperText="Ex: Segunda a Sexta, 9h às 18h"
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Mensagens do Modal
        </Typography>
        <TextField
          fullWidth
          label="Título do modal"
          value={config.messages?.developmentFeatureTitle || ''}
          onChange={e =>
            setConfig({
              ...config,
              messages: { ...config.messages, developmentFeatureTitle: e.target.value }
            })
          }
          margin="normal"
          helperText="Ex: Funcionalidade em Desenvolvimento"
        />
        <TextField
          fullWidth
          label="Descrição da funcionalidade"
          multiline
          rows={2}
          value={config.messages?.developmentFeatureDescription || ''}
          onChange={e =>
            setConfig({
              ...config,
              messages: { ...config.messages, developmentFeatureDescription: e.target.value }
            })
          }
          margin="normal"
          helperText="Texto exibido quando o usuário clica em NF ou Boleto"
        />
        <TextField
          fullWidth
          label="Instrução para contato"
          value={config.messages?.contactInstruction || ''}
          onChange={e =>
            setConfig({
              ...config,
              messages: { ...config.messages, contactInstruction: e.target.value }
            })
          }
          margin="normal"
          helperText="Ex: Para ativar sua assinatura, entre em contato com nosso suporte:"
        />
        <TextField
          fullWidth
          label="Mensagem de garantia"
          multiline
          rows={2}
          value={config.messages?.assuranceMessage || ''}
          onChange={e =>
            setConfig({
              ...config,
              messages: { ...config.messages, assuranceMessage: e.target.value }
            })
          }
          margin="normal"
          helperText="Ex: Nossa equipe irá ajudá-lo a processar o pagamento e ativar sua assinatura imediatamente."
        />

        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 3 }}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </Paper>
    </Box>
  );
};

export default SupportConfigPage;
